import { useContext, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { SkusServerProps } from '@/types/props/pages';
import useBackendUser from '@/hooks/useBackendUser';
import useWindowWidth from '@/hooks/useWindowWidth';
import { WishlistContext } from '@/context/wishlistContext';
import SkusSearchLayout from './SkusSearchLayout';
import ResponsiveComponent from '../common/ResponsiveComponent';
import SkusDesktop from './SkusDesktop';
import SkusMobile from './SkusMobile/SkusMobile';
import SkusBrandContent from './SkusBrandContent';
import { AllSortsCustomTranslated } from '@/types/other';
import { SEARCH_HITS_PER_PAGE, SORT_TOKENS, virtualIndex } from '@/constants/search.constants';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { CommonEnum } from '@/types/translations/CommonEnum';
import LandingPage from '@/components/landing-pages/LandingPage';
import { Configure } from 'react-instantsearch';

const createSortOptions = (translate: (key: CommonEnum) => string): AllSortsCustomTranslated[] => [
  {
    label: translate(CommonEnum.most_popular),
    value: virtualIndex(SORT_TOKENS.mostPopular),
    name: translate(CommonEnum.most_popular),
  },
  {
    label: translate(CommonEnum.relevance),
    value: virtualIndex(SORT_TOKENS.relevance),
    name: translate(CommonEnum.relevance),
  },
  {
    label: translate(CommonEnum.most_watched),
    value: virtualIndex(SORT_TOKENS.mostWatched),
    name: translate(CommonEnum.most_watched),
  },
  {
    label: translate(CommonEnum.newest),
    value: virtualIndex(SORT_TOKENS.newest),
    name: translate(CommonEnum.newest),
  },
  {
    label: translate(CommonEnum.lowest_ask_sort),
    value: virtualIndex(SORT_TOKENS.lowestAskAsc),
    name: translate(CommonEnum.lowest_ask_sort),
  },
  {
    label: translate(CommonEnum.highest_ask),
    value: virtualIndex(SORT_TOKENS.highestAskDesc),
    name: translate(CommonEnum.highest_ask),
  },
  {
    label: translate(CommonEnum.lowest_bid),
    value: virtualIndex(SORT_TOKENS.lowestBidAsc),
    name: translate(CommonEnum.lowest_bid),
  },
  {
    label: translate(CommonEnum.highest_bid),
    value: virtualIndex(SORT_TOKENS.highestBidDesc),
    name: translate(CommonEnum.highest_bid),
  },
  {
    label: translate(CommonEnum.newest_bids),
    value: virtualIndex(SORT_TOKENS.newestBid),
    name: translate(CommonEnum.newest_bids),
  },
];

const SkusEntirePage = ({
  windowWidthSv,
  metaTitle,
  h1Title,
  brandsContent,
  hasFilterCookie,
  landingPage,
  landingPageFilter,
  noIndex,
  localeAlternates,
}: SkusServerProps) => {
  const { t } = useTranslation('index');
  const { t: tCommon } = useTranslation('common');
  const { translate } = useTypedTranslation<CommonEnum>('common');

  const { data: user = null } = useBackendUser();
  const windowWidth = useWindowWidth(windowWidthSv);
  const wishlistContext = useContext(WishlistContext);

  // Memoize sort options to prevent recreation on every render
  const allSortsCustomTranslated = useMemo(() => createSortOptions(translate), [translate]);

  // Memoize expensive computations
  const hasBannerImage = useMemo(
    () => Boolean(brandsContent?.banner_image),
    [brandsContent?.banner_image]
  );

  // Memoize desktop component props to prevent unnecessary re-renders
  const desktopProps = useMemo(
    () => ({
      allSortsCustomTranslated,
      tCommon,
      wishlistContext,
      user,
      windowWidth,
      h1Title,
      brandsContent,
    }),
    [allSortsCustomTranslated, tCommon, wishlistContext, user, windowWidth, h1Title, brandsContent]
  );

  // Memoize mobile component props
  const mobileProps = useMemo(
    () => ({
      allSortsCustomTranslated,
      hasFilterCookie,
      brandsContent,
    }),
    [allSortsCustomTranslated, hasFilterCookie, brandsContent]
  );

  // Memoize search layout props
  const searchLayoutProps = useMemo(
    () => ({
      tCommon,
      t,
      metaTitle,
      brandsContent,
      noIndex,
      localeAlternates,
      // A landing page's own SEO copy wins over the listing default (WEB-1639).
      pageTitleOverride: landingPage?.meta_title ?? undefined,
      metaDescription: landingPage?.meta_description ?? undefined,
      metaKeywords: landingPage?.meta_keywords ?? undefined,
    }),
    [tCommon, t, metaTitle, brandsContent, noIndex, localeAlternates, landingPage]
  );

  // Memoize the main content to avoid recreation
  const mainContent = useMemo(
    () => (
      <>
        <Configure hitsPerPage={SEARCH_HITS_PER_PAGE} filters={landingPageFilter ?? undefined} />
        <ResponsiveComponent
          windowWidth={windowWidth}
          DesktopComponent={<SkusDesktop {...desktopProps} includeCategoryNav={!landingPage} />}
          MobileComponent={<SkusMobile {...mobileProps} />}
        />
      </>
    ),
    [windowWidth, desktopProps, mobileProps, landingPage, landingPageFilter]
  );

  // Memoize brand content component
  const brandContentComponent = useMemo(
    () => (hasBannerImage ? <SkusBrandContent brandsContent={brandsContent} /> : null),
    [hasBannerImage, brandsContent]
  );

  if (landingPage) {
    return (
      <LandingPage landingPage={landingPage}>
        <div>
          <SkusSearchLayout {...searchLayoutProps}>{mainContent}</SkusSearchLayout>
          {brandContentComponent}
        </div>
      </LandingPage>
    );
  }

  return (
    <div>
      <SkusSearchLayout {...searchLayoutProps}>{mainContent}</SkusSearchLayout>
      {brandContentComponent}
    </div>
  );
};

export default SkusEntirePage;
